import {
  createEmptyRirekishoFormatData,
  type RirekishoFormatData
} from '@/composables/format-edit/useRirekishoFormatData'

export const DEFAULT_MYPROFILE_TYPES = ['インドア', 'ご飯', '夏', 'いぬ']

const FAVORITES_LENGTH = 6
const BEST3_LENGTH = 3
const TYPES_LENGTH = 4

function emptyStrings(length: number, defaults?: string[]): string[] {
  const next: string[] = []
  for (let i = 0; i < length; i++) {
    next.push(defaults?.[i] ?? '')
  }
  return next
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function padStringArray(
  value: string[],
  length: number,
  defaults?: string[]
): string[] {
  const next = value.slice()
  while (next.length < length) {
    next.push(defaults?.[next.length] ?? '')
  }
  return next
}

export function stringListItem(list: unknown, index: number): string {
  if (!Array.isArray(list)) return ''
  const item = list[index]
  return typeof item === 'string' ? item : ''
}

function writeFormatField(
  data: Record<string, unknown>,
  key: string,
  value: unknown
): void {
  data[key] = value
}

function toInputString(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '')
}

function ensureStringArray(
  formatData: RirekishoFormatData,
  key: string,
  length: number,
  defaults?: string[]
): string[] {
  const current = formatData[key]
  if (isStringArray(current)) {
    if (current.length >= length) return current
    const next = padStringArray(current, length, defaults)
    writeFormatField(formatData as Record<string, unknown>, key, next)
    return next
  }
  if (Array.isArray(current)) {
    return current.map((item) => toInputString(item))
  }
  const next = emptyStrings(length, defaults)
  writeFormatField(formatData as Record<string, unknown>, key, next)
  return next
}

export function setStringListItem(
  formatData: RirekishoFormatData,
  key: string,
  index: number,
  value: unknown,
  length: number,
  defaults?: string[]
): void {
  const list = ensureStringArray(formatData, key, length, defaults)
  // Vue 2 は arr[i] = v を検知しない（旧実装は $set）。配列ごと差し替える。
  const next = list.slice()
  next[Number(index)] = toInputString(value)
  writeFormatField(formatData as Record<string, unknown>, key, next)
}

function ensureLoadedStringList(
  data: RirekishoFormatData,
  key: string,
  length: number,
  defaults?: string[]
): void {
  const current = data[key]
  if (current == null) {
    writeFormatField(
      data as Record<string, unknown>,
      key,
      emptyStrings(length, defaults)
    )
    return
  }
  if (isStringArray(current) && current.length < length) {
    writeFormatField(
      data as Record<string, unknown>,
      key,
      padStringArray(current, length, defaults)
    )
  }
}

export function createEmptyMyprofileFormatData(): RirekishoFormatData {
  const data = createEmptyRirekishoFormatData()
  data.favorites = emptyStrings(FAVORITES_LENGTH)
  // マイプロフィール BEST3 は文字列配列。履歴書の hobbies/skills 型とは共有キーで衝突するが、
  // localforage のキーは現行どおり変えない。
  writeFormatField(
    data as Record<string, unknown>,
    'hobbies',
    emptyStrings(BEST3_LENGTH)
  )
  writeFormatField(
    data as Record<string, unknown>,
    'skills',
    emptyStrings(BEST3_LENGTH)
  )
  data.types = emptyStrings(TYPES_LENGTH, DEFAULT_MYPROFILE_TYPES)
  data.myboom = ''
  data.holiday = ''
  data.wanted = ''
  data.freespace = ''
  return data
}

export function migrateMyprofileFormatData(
  data: RirekishoFormatData
): RirekishoFormatData {
  const next = { ...data } as RirekishoFormatData
  ensureLoadedStringList(next, 'favorites', FAVORITES_LENGTH)
  ensureLoadedStringList(next, 'types', TYPES_LENGTH, DEFAULT_MYPROFILE_TYPES)
  if (next.myboom == null) next.myboom = ''
  if (next.holiday == null) next.holiday = ''
  if (next.wanted == null) next.wanted = ''
  if (next.freespace == null) next.freespace = ''
  return next
}
